import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import FileUploadManager from '@/components/FileUploadManager';
import PrintableDocument from '@/components/PrintableDocument';
import CorrespondenceTab from '@/components/correspondence/CorrespondenceTab';
import SendToTaskGenerator from '@/components/correspondence/SendToTaskGenerator';
import DocumentGeneratorButton from '@/components/DocumentGeneratorButton';
import { api, getErrorMessage } from '@/api';
import { toast } from 'sonner';
import { AlertCircle, Edit3, Save, Send, FileText, Loader2, Mail, Trash2, Calendar, Upload, CheckCircle, RefreshCw, XCircle } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import usePermissions from '@/hooks/usePermissions';
import { useTerminology } from '@/hooks/useTerminology';

export default function NoticeDetail() {
  const { noticeId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [notice, setNotice] = useState(null);
  const [loading, setLoading] = useState(true);
  const noticeTerminology = useTerminology(notice?.project_id);
  const noticeLabelText = noticeTerminology.noticeLabel;
  const claimLabelForNotice = noticeTerminology.claimLabel;
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editedContent, setEditedContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [impactType, setImpactType] = useState('time_and_cost');
  const [saving, setSaving] = useState(false);
  const [converting, setConverting] = useState(false);
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitType, setSubmitType] = useState('both');
  const [additionalEmails, setAdditionalEmails] = useState('');
  const [sharing, setSharing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [project, setProject] = useState(null);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmMessage, setConfirmMessage] = useState('');
  const [showCeasedForm, setShowCeasedForm] = useState(false);
  const [ceasedDate, setCeasedDate] = useState('');
  const [ceasedReason, setCeasedReason] = useState('');
  const [ceasedAttachments, setCeasedAttachments] = useState([]);
  const [markingCeased, setMarkingCeased] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [voidDialogOpen, setVoidDialogOpen] = useState(false);
  const [voidReason, setVoidReason] = useState('');
  const [voiding, setVoiding] = useState(false);
  const [aiInstructions, setAiInstructions] = useState('');
  const [showAiEdit, setShowAiEdit] = useState(false);
  const [daysClaimed, setDaysClaimed] = useState('');

  const isAdmin = user?.role === 'admin';
  const permissions = usePermissions();

  useEffect(() => {
    loadNotice();
  }, [noticeId]);

  const loadNotice = async () => {
    try {
      const response = await api.notices.getById(noticeId);
      setNotice(response.data);
      setEditedContent(response.data.edited_content);
      setAttachments(response.data.attachments || []);
      setImpactType(response.data.impact_type || 'time_and_cost');
      setDaysClaimed(response.data.days_claimed || '');
      setError(null);
      
      // Load project info for print header
      if (response.data.project_id) {
        try {
          const projectRes = await api.projects.getById(response.data.project_id);
          setProject(projectRes.data);
        } catch (e) {
          console.error('Failed to load project:', e);
        }
      }
    } catch (err) {
      console.error('Failed to load notice:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to load notice';
      setError(errorMessage);
      if (err.response?.status === 403) {
        toast.error('You do not have permission to view this notice');
      } else if (err.response?.status === 404) {
        toast.error('Notice not found');
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.notices.update(noticeId, { 
        edited_content: editedContent,
        attachments: attachments,
        impact_type: impactType,
        days_claimed: daysClaimed ? parseInt(daysClaimed) : null
      });
      toast.success('Notice updated successfully');
      setEditing(false);
      loadNotice();
    } catch (error) {
      toast.error('Failed to update notice');
    } finally {
      setSaving(false);
    }
  };

  const handleConvertToClaim = async (isProspective = false) => {
    setConverting(true);
    try {
      const response = await api.notices.convertToClaim(noticeId, isProspective);
      
      // Check if confirmation is required (delay hasn't ceased)
      if (response.data.requires_confirmation && !isProspective) {
        // Show our custom confirmation dialog instead of window.confirm
        setConfirmMessage(response.data.message);
        setConfirmDialogOpen(true);
        setConverting(false);
        return;
      }
      
      if (response.data.claim_id) {
        toast.success('EOT Claim generated successfully');
        navigate(`/claims/${response.data.claim_id}`);
      } else {
        toast.error('Failed to generate claim - no claim ID returned');
        setConverting(false);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to generate claim'));
      setConverting(false);
    }
  };

  const handleConfirmProspectiveClaim = async () => {
    setConfirmDialogOpen(false);
    await handleConvertToClaim(true);
  };

  const handleMarkCeasedAndConvert = async () => {
    if (!ceasedDate) {
      toast.error('Please enter the date the delay ceased');
      return;
    }

    setMarkingCeased(true);
    try {
      // First, mark the delay as ceased
      await api.events.markCeasedWithDetails(notice.event_id, {
        ceased_date: ceasedDate,
        reason: ceasedReason || null,
        attachments: ceasedAttachments.length > 0 ? ceasedAttachments : null
      });
      
      toast.success('Delay marked as ceased');
      
      // Reset the form
      setShowCeasedForm(false);
      setCeasedDate('');
      setCeasedReason('');
      setCeasedAttachments([]);
      setConfirmDialogOpen(false);
      
      // Now convert to claim (non-prospective since delay has ceased)
      setConverting(true);
      const response = await api.notices.convertToClaim(noticeId, false);
      
      if (response.data.claim_id) {
        toast.success('EOT Claim generated successfully');
        navigate(`/claims/${response.data.claim_id}`);
      } else {
        toast.error('Failed to generate claim');
        setConverting(false);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to mark delay as ceased'));
    } finally {
      setMarkingCeased(false);
    }
  };

  const handleSubmit = async () => {
    setSharing(true);
    try {
      const emails = additionalEmails
        .split(',')
        .map(e => e.trim())
        .filter(e => e);
      await api.notices.share(noticeId, {
        share_type: submitType,
        additional_emails: emails.length > 0 ? emails : null,
      });
      toast.success('Notice submitted successfully');
      setSubmitOpen(false);
      loadNotice();
    } catch (error) {
      toast.error('Failed to submit notice');
    } finally {
      setSharing(false);
    }
  };

  const handleRegenerateContent = async (withInstructions = false) => {
    setRegenerating(true);
    try {
      const response = await api.post(`/notices/${noticeId}/regenerate`, {
        ai_instructions: withInstructions ? aiInstructions : null
      });
      if (response.data.success) {
        toast.success(withInstructions 
          ? 'Notice updated with your edits' 
          : 'Notice content regenerated successfully'
        );
        // Update the local state with new content
        setNotice(prev => ({
          ...prev,
          generated_content: response.data.generated_content
        }));
        setEditedContent(response.data.generated_content);
        setAiInstructions('');
        setShowAiEdit(false);
      }
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to regenerate content'));
    } finally {
      setRegenerating(false);
    }
  };

  const handleVoidNotice = async () => {
    if (!voidReason.trim()) {
      toast.error('Please provide a reason for voiding this notice');
      return;
    }
    
    setVoiding(true);
    try {
      const response = await api.post(`/notices/${noticeId}/void`, { void_reason: voidReason });
      if (response.data.success) {
        toast.success('Notice voided successfully');
        setVoidDialogOpen(false);
        setVoidReason('');
        loadNotice();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to void notice');
    } finally {
      setVoiding(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/notices/${noticeId}`);
      toast.success('Notice deleted permanently');
      setDeleteDialogOpen(false);
      navigate(`/projects/${notice.project_id || ''}`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete notice';
      toast.error(message);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <p className="text-slate-500 font-mono">Loading notice...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!notice) {
    return (
      <DashboardLayout>
        <div className="p-8">
          <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-12 text-center">
            <AlertCircle className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <h3 className="text-xl font-barlow font-bold uppercase mb-2">
              {error?.includes('permission') || error?.includes('Access denied') 
                ? 'Access Denied' 
                : 'Notice Not Found'}
            </h3>
            {error && (
              <p className="text-slate-500 mb-4">{error}</p>
            )}
            <Button onClick={() => navigate(-1)} className="mt-4">
              Go Back
            </Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const getStatusBadge = (status) => {
    const styles = {
      draft: 'bg-orange-100 text-orange-700 border-orange-200',
      submitted: 'bg-blue-100 text-blue-700 border-blue-200',
      approved: 'bg-green-100 text-green-700 border-green-200',
      rejected: 'bg-red-100 text-red-700 border-red-200',
    };
    return styles[status] || styles.draft;
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="notice-detail">
        <div className="mb-8">
          <Button
            onClick={() => navigate(`/projects/${notice.project_id}`)}
            variant="ghost"
            className="mb-4 text-slate-600 hover:text-slate-900"
          >
            ← Back to Project
          </Button>
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-barlow font-bold uppercase tracking-tight mb-2">
                {notice.notice_number || 'Delay Notice'}
              </h1>
              <div className="flex items-center gap-2 sm:gap-3 text-xs sm:text-sm text-slate-600 flex-wrap">
                <Badge className={`${getStatusBadge(notice.status)} border`}>
                  {notice.status.toUpperCase()}
                </Badge>
                {!notice.notice_number && (
                  <>
                    <span className="hidden sm:inline">•</span>
                    <span className="font-mono">ID: {notice.id.substring(0, 8)}</span>
                  </>
                )}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              {!editing ? (
                <>
                  {/* Edit button - hide if submitted or has claim */}
                  {permissions.canEditNotices() && notice.status !== 'submitted' && !notice.claim_id && (
                    <Button
                      onClick={() => setEditing(true)}
                      data-testid="edit-notice-button"
                      variant="outline"
                      className="border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"
                    >
                      <Edit3 className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">Edit</span>
                    </Button>
                  )}
                  <SendToTaskGenerator
                    projectId={notice.project_id}
                    moduleType="Notice"
                    moduleEntityId={notice.id}
                    moduleReference={notice.notice_number}
                  />
                  <DocumentGeneratorButton
                    documentType="Notice"
                    projectId={notice.project_id}
                    entityId={notice.id}
                    entityData={notice}
                    projectData={project}
                  />
                  {/* AI Edit and Regenerate - hide if submitted or has claim */}
                  {permissions.canEditNotices() && notice.status !== 'submitted' && !notice.claim_id && (
                    <>
                      {/* AI Edit Button - Only show if not yet converted to claim */}
                      <Button
                        onClick={() => setShowAiEdit(true)}
                        disabled={regenerating}
                        data-testid="ai-edit-notice-button"
                        variant="outline"
                        className="border-purple-600 text-purple-600 hover:bg-purple-600 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"
                      >
                        <Edit3 className="h-4 w-4 sm:mr-2" />
                        <span className="hidden sm:inline">AI Edit</span>
                      </Button>
                      {/* Regenerate Button - Only show if not yet converted to claim */}
                      <Button
                        onClick={() => handleRegenerateContent(false)}
                        disabled={regenerating}
                        data-testid="regenerate-notice-button"
                        variant="outline"
                        className="border-blue-600 text-blue-600 hover:bg-blue-600 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"
                      >
                        {regenerating ? (
                          <Loader2 className="h-4 w-4 animate-spin sm:mr-2" />
                        ) : (
                          <RefreshCw className="h-4 w-4 sm:mr-2" />
                        )}
                        <span className="hidden sm:inline">{regenerating ? 'Regenerating...' : 'Regenerate'}</span>
                      </Button>
                    </>
                  )}

                  {/* AI Edit Dialog */}
                  <Dialog open={showAiEdit} onOpenChange={setShowAiEdit}>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle className="text-xl font-barlow font-bold uppercase flex items-center gap-2">
                          <Edit3 className="h-5 w-5 text-purple-600" />
                          AI-Assisted Edit
                        </DialogTitle>
                        <DialogDescription>
                          Describe the specific edits you want. The AI will ONLY change what you mention - it won't rewrite the entire notice.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                            What do you want to edit?
                          </Label>
                          <Textarea
                            value={aiInstructions}
                            onChange={(e) => setAiInstructions(e.target.value)}
                            placeholder="Examples of targeted edits:
• Change the date in paragraph 3 from 15 March to 18 March
• Add a sentence about weather conditions in the timeline section
• Update the claimed days from 5 to 7 days
• Strengthen the language in the entitlement section
• Add reference to email from John dated 20 March"
                            rows={6}
                            className="rounded-sm text-sm"
                          />
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                          <p className="text-xs text-amber-700">
                            <strong>Note:</strong> AI will only edit the specific parts you mention. For full manual control, use the "Edit" button instead.
                          </p>
                        </div>
                        <div className="flex gap-2 justify-end">
                          <Button
                            variant="outline"
                            onClick={() => setShowAiEdit(false)}
                            disabled={regenerating}
                          >
                            Cancel
                          </Button>
                          <Button
                            onClick={() => handleRegenerateContent(true)}
                            disabled={regenerating || !aiInstructions.trim()}
                            className="bg-purple-600 text-white hover:bg-purple-700"
                          >
                            {regenerating ? (
                              <>
                                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                                Applying Edits...
                              </>
                            ) : (
                              <>
                                <Edit3 className="h-4 w-4 mr-2" />
                                Apply Edits
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                  {/* Submit button - hide if already submitted */}
                  {notice.status !== 'submitted' && (
                    <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
                      <DialogTrigger asChild>
                        <Button
                          data-testid="submit-notice-button"
                          variant="outline"
                          className="border-secondary text-secondary hover:bg-secondary hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"
                        >
                          <Send className="h-4 w-4 sm:mr-2" />
                          <span className="hidden sm:inline">Submit</span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-xl font-barlow font-bold uppercase">Submit {noticeLabelText}</DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Submit To</Label>
                            <Select value={submitType} onValueChange={setSubmitType}>
                            <SelectTrigger className="rounded-sm">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="internal">Internal Team Only</SelectItem>
                              <SelectItem value="external">External (Principal Agent / Professional Team)</SelectItem>
                              <SelectItem value="both">Both Internal & External</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Additional Emails (comma separated)</Label>
                          <Input
                            value={additionalEmails}
                            onChange={(e) => setAdditionalEmails(e.target.value)}
                            placeholder="email1@example.com, email2@example.com"
                            className="rounded-sm"
                          />
                        </div>
                        <Button
                          onClick={handleSubmit}
                          disabled={sharing}
                          className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                        >
                          {sharing ? 'Submitting...' : `Submit ${noticeLabelText}`}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  )}
                  {/* Convert to Claim - show for submitted/sent notices that haven't been converted yet */}
                  {permissions.canConvertNoticeToClaim() && (notice.status === 'submitted' || notice.status === 'sent') && !notice.claim_id && (
                    <Button
                      onClick={() => handleConvertToClaim()}
                      disabled={converting}
                      data-testid="convert-to-claim-button"
                      className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"
                    >
                      {converting ? (
                        <>
                          <Loader2 className="h-4 w-4 sm:mr-2 animate-spin" />
                          <span className="hidden sm:inline">Generating...</span>
                        </>
                      ) : (
                        <>
                          <FileText className="h-4 w-4 sm:mr-2" />
                          <span className="hidden sm:inline">Convert to EOT {claimLabelForNotice}</span>
                          <span className="sm:hidden">Convert</span>
                        </>
                      )}
                    </Button>
                  )}
                  
                  {/* Show link to claim if already converted */}
                  {notice.claim_id && (
                    <Button
                      onClick={() => navigate(`/claims/${notice.claim_id}`)}
                      variant="outline"
                      className="border-green-600 text-green-600 hover:bg-green-600 hover:text-white rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"
                      data-testid="view-claim-button"
                    >
                      <CheckCircle className="h-4 w-4 sm:mr-2" />
                      <span className="hidden sm:inline">View {claimLabelForNotice}</span>
                      <span className="sm:hidden">{claimLabelForNotice}</span>
                    </Button>
                  )}
                  
                  {/* Void Button - Contractors can void their notices */}
                  {permissions.canEditNotices() && notice.status !== 'voided' && !notice.claim_id && (
                    <Dialog open={voidDialogOpen} onOpenChange={setVoidDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="border-orange-400 text-orange-600 hover:bg-orange-50 rounded-sm font-medium uppercase tracking-wide text-xs px-3 sm:px-6 py-2 sm:py-3"
                          data-testid="void-notice-button"
                        >
                          <XCircle className="h-4 w-4 sm:mr-2" />
                          <span className="hidden sm:inline">Void</span>
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-xl font-barlow font-bold uppercase text-orange-600">Void Notice</DialogTitle>
                          <DialogDescription>
                            Voiding this notice will mark it as invalid. You will be able to create a new notice for the delay event.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Reason for Voiding *</Label>
                            <Textarea
                              value={voidReason}
                              onChange={(e) => setVoidReason(e.target.value)}
                              placeholder="Enter the reason for voiding this notice..."
                              className="rounded-sm min-h-[100px]"
                            />
                          </div>
                          <div className="flex gap-2">
                            <Button
                              onClick={() => setVoidDialogOpen(false)}
                              variant="outline"
                              className="flex-1 rounded-sm"
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={handleVoidNotice}
                              disabled={voiding || !voidReason.trim()}
                              className="flex-1 bg-orange-600 text-white hover:bg-orange-700 rounded-sm"
                            >
                              {voiding ? 'Voiding...' : 'Void Notice'}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  {/* Delete Button - Permission Based */}
                  {permissions.can('notices', 'delete') && !notice.claim_id && (
                    <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                      <DialogTrigger asChild>
                        <Button
                          variant="outline"
                          className="border-red-300 text-red-600 hover:bg-red-50 rounded-sm font-medium uppercase tracking-wide text-xs px-4 py-3"
                          data-testid="delete-notice-button"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle className="text-xl font-barlow font-bold uppercase text-red-600">Delete Notice</DialogTitle>
                          <DialogDescription>
                            This will permanently delete this notice and update the parent event.
                          </DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4">
                          <div className="bg-red-50 border border-red-200 rounded-sm p-4">
                            <p className="text-sm text-red-700">
                              <strong>Warning:</strong> This action cannot be undone. The parent event will be updated to remove the notice reference.
                            </p>
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                              Cancel
                            </Button>
                            <Button
                              onClick={handleDelete}
                              disabled={deleting}
                              className="bg-red-600 text-white hover:bg-red-700"
                            >
                              {deleting ? 'Deleting...' : 'Delete Notice'}
                            </Button>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )}
                  
                  {/* Prospective Claim Confirmation Dialog */}
                  <Dialog open={confirmDialogOpen} onOpenChange={(open) => {
                    setConfirmDialogOpen(open);
                    if (!open) {
                      setShowCeasedForm(false);
                      setCeasedDate('');
                      setCeasedReason('');
                      setCeasedAttachments([]);
                    }
                  }}>
                    <DialogContent className="w-[95vw] max-w-md sm:max-w-lg p-4 sm:p-6">
                      <DialogHeader>
                        <DialogTitle className="text-lg sm:text-xl font-barlow font-bold uppercase">
                          {showCeasedForm ? 'Mark Delay as Ceased' : 'Confirm Prospective Claim'}
                        </DialogTitle>
                        <DialogDescription className="text-sm">
                          {showCeasedForm 
                            ? 'Enter the details of when and why the delay ceased.'
                            : confirmMessage
                          }
                        </DialogDescription>
                      </DialogHeader>
                      
                      {showCeasedForm ? (
                        // Mark Delay as Ceased Form
                        <div className="space-y-4">
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                              Date Delay Ceased <span className="text-red-500">*</span>
                            </Label>
                            <Input
                              type="date"
                              value={ceasedDate}
                              onChange={(e) => setCeasedDate(e.target.value)}
                              className="rounded-sm"
                              max={new Date().toISOString().split('T')[0]}
                              data-testid="ceased-date-input"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                              Reason Why Delay Ceased
                            </Label>
                            <Textarea
                              value={ceasedReason}
                              onChange={(e) => setCeasedReason(e.target.value)}
                              placeholder="e.g., Weather conditions improved, Materials delivered..."
                              rows={3}
                              className="rounded-sm text-sm"
                              data-testid="ceased-reason-input"
                            />
                          </div>
                          
                          <div>
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">
                              Supporting Documents (Optional)
                            </Label>
                            <p className="text-xs text-slate-500 mb-2">
                              Upload emails (.msg), photos, or other evidence.
                            </p>
                            <FileUploadManager
                              attachments={ceasedAttachments}
                              onAttachmentsChange={setCeasedAttachments}
                              allowedTypes={['photo', 'email', 'drawing', 'site_instruction', 'other']}
                              compact={true}
                            />
                          </div>
                          
                          <div className="flex flex-col-reverse sm:flex-row gap-2 justify-end pt-4 border-t">
                            <Button 
                              variant="outline" 
                              onClick={() => setShowCeasedForm(false)}
                              disabled={markingCeased}
                              className="w-full sm:w-auto"
                            >
                              Back
                            </Button>
                            <Button
                              onClick={handleMarkCeasedAndConvert}
                              disabled={markingCeased || !ceasedDate}
                              className="w-full sm:w-auto bg-green-600 text-white hover:bg-green-700"
                              data-testid="mark-ceased-submit-button"
                            >
                              {markingCeased ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Processing...
                                </>
                              ) : (
                                <>
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  <span className="hidden sm:inline">Mark Ceased & Generate Claim</span>
                                  <span className="sm:hidden">Mark Ceased & Generate</span>
                                </>
                              )}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // Original Prospective Claim Options
                        <div className="space-y-3 sm:space-y-4">
                          <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 sm:p-4">
                            <p className="text-sm text-amber-700 font-medium mb-1">
                              The delay event has not yet ceased.
                            </p>
                            <p className="text-xs sm:text-sm text-amber-600">
                              Proceed with a prospective claim, or mark the delay as ceased first.
                            </p>
                          </div>
                          
                          <div className="grid gap-2 sm:gap-3">
                            <Button
                              onClick={() => setShowCeasedForm(true)}
                              variant="outline"
                              className="w-full justify-start h-auto py-3 sm:py-4 border-green-200 hover:bg-green-50"
                              data-testid="mark-ceased-option-button"
                            >
                              <div className="flex items-start gap-2 sm:gap-3">
                                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 mt-0.5 flex-shrink-0" />
                                <div className="text-left min-w-0">
                                  <p className="font-medium text-green-700 text-sm sm:text-base">Mark Delay as Ceased First</p>
                                  <p className="text-xs text-slate-500 mt-0.5 sm:mt-1">
                                    Record the ceased date and reason before generating.
                                  </p>
                                </div>
                              </div>
                            </Button>
                            
                            <Button
                              onClick={handleConfirmProspectiveClaim}
                              disabled={converting}
                              className="w-full justify-start h-auto py-3 sm:py-4 bg-slate-900 hover:bg-slate-800"
                              data-testid="confirm-prospective-claim-button"
                            >
                              <div className="flex items-start gap-2 sm:gap-3">
                                <FileText className="h-4 w-4 sm:h-5 sm:w-5 mt-0.5 flex-shrink-0" />
                                <div className="text-left min-w-0">
                                  <p className="font-medium text-sm sm:text-base">Proceed with Prospective Claim</p>
                                  <p className="text-xs text-slate-300 mt-0.5 sm:mt-1">
                                    Generate claim while delay is still ongoing.
                                  </p>
                                </div>
                              </div>
                            </Button>
                          </div>
                          
                          <div className="flex justify-end pt-2">
                            <Button 
                              variant="ghost" 
                              onClick={() => setConfirmDialogOpen(false)}
                              disabled={converting}
                              size="sm"
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </DialogContent>
                  </Dialog>
                </>
              ) : (
                <Button
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-notice-button"
                  className="bg-slate-900 text-white hover:bg-slate-800 rounded-sm font-medium uppercase tracking-wide text-xs px-6 py-3"
                >
                  {saving ? 'Saving...' : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Notice Information Grid */}
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-4 sm:p-6 mb-6">
          <h3 className="text-lg font-barlow font-bold uppercase tracking-tight mb-4 text-slate-800 border-b pb-2">
            Notice Information
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Project Info */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Project Name</Label>
              <p className="text-sm font-medium text-slate-900">{project?.name || '-'}</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Project Number</Label>
              <p className="text-sm font-medium text-slate-900">{project?.project_number || '-'}</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Contract Type</Label>
              <p className="text-sm font-medium text-slate-900">{project?.contract_type || 'JBCC'}</p>
            </div>
            
            {/* Notice Info */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Notice Number</Label>
              <p className="text-sm font-medium text-slate-900">{notice.notice_number || '-'}</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Notice Date</Label>
              <p className="text-sm font-medium text-slate-900">
                {notice.created_at ? new Date(notice.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
              </p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Status</Label>
              <div className="flex items-center gap-2">
                <Badge className="text-xs">
                  {notice.status?.replace('_', ' ').toUpperCase() || 'DRAFT'}
                </Badge>
                {notice.submitted_at && (
                  <span className="text-xs text-slate-500">
                    ({new Date(notice.submitted_at).toLocaleDateString('en-ZA')})
                  </span>
                )}
              </div>
            </div>
            
            {/* Submitted By - Auto populated */}
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Submitted By</Label>
              <p className="text-sm font-medium text-slate-900">
                {notice.submitted_by || notice.created_by || user?.name || '-'}
              </p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Impact Type</Label>
              <p className="text-sm font-medium text-slate-900">{impactType?.replace('_', ' & ').replace(/\b\w/g, l => l.toUpperCase()) || 'Time & Cost'}</p>
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">Days Claimed</Label>
              {editing ? (
                <Input
                  type="number"
                  value={daysClaimed}
                  onChange={(e) => setDaysClaimed(e.target.value)}
                  placeholder="e.g., 14"
                  className="rounded-sm h-9 w-32"
                />
              ) : (
                <p className="text-sm font-medium text-slate-900">
                  {notice.days_claimed ? `${notice.days_claimed} Working Days` : 'TBD'}
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-8">
          <h2 className="text-xl font-barlow font-bold uppercase tracking-tight mb-6 pb-4 border-b border-slate-100">Notice Content</h2>
          {editing ? (
            <div className="space-y-6">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 block">Edit Notice</Label>
                <Textarea
                  value={editedContent}
                  onChange={(e) => setEditedContent(e.target.value)}
                  rows={20}
                  className="rounded-sm font-sans"
                />
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 block">Impact Type</Label>
                <div className="flex gap-4">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="time"
                      checked={impactType === 'time'}
                      onCheckedChange={(checked) => checked && setImpactType('time')}
                    />
                    <label htmlFor="time" className="text-sm font-medium cursor-pointer">
                      Time Impact Only
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cost"
                      checked={impactType === 'cost'}
                      onCheckedChange={(checked) => checked && setImpactType('cost')}
                    />
                    <label htmlFor="cost" className="text-sm font-medium cursor-pointer">
                      Cost Impact Only
                    </label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="time_and_cost"
                      checked={impactType === 'time_and_cost'}
                      onCheckedChange={(checked) => checked && setImpactType('time_and_cost')}
                    />
                    <label htmlFor="time_and_cost" className="text-sm font-medium cursor-pointer">
                      Time & Cost Impact
                    </label>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-3 block">Supporting Documents</Label>
                <FileUploadManager
                  attachments={attachments}
                  onAttachmentsChange={setAttachments}
                  allowedTypes={['photo', 'email', 'drawing', 'site_instruction', 'other']}
                />
              </div>
            </div>
          ) : (
            <div className="prose max-w-none">
              <div className="whitespace-pre-wrap text-slate-700 leading-relaxed">
                {notice.edited_content}
              </div>
            </div>
          )}
        </div>

        {notice.status === 'draft' && (
          <div className="bg-orange-50 border border-orange-200 rounded-sm p-6 mt-6">
            <h4 className="text-sm font-bold uppercase tracking-wider text-orange-700 mb-2">Reminder</h4>
            <p className="text-sm text-orange-600">
              Notice must be sent within 20 days of becoming aware of the delay (JBCC requirement). Review, edit if necessary, and share with relevant parties.
            </p>
          </div>
        )}

        {/* Email Correspondence Section */}
        <div className="mt-8 bg-white border border-slate-200 rounded-sm p-6" data-testid="notice-correspondence-section">
          <CorrespondenceTab 
            projectId={notice.project_id}
            moduleType="Notice"
            moduleEntityId={noticeId}
            title="Email Correspondence"
          />
        </div>
      </div>
    </DashboardLayout>
  );
}